import { JsonSerializable } from '@/util/object/JsonSerializable'
import { FulfillmentStatus, FulfillmentType, ItemDeliveryStatus, ItemRewardType } from '@/cowboy/EnumTypes'
import { DirectPaymentMetadataResponse } from '@/cowboy/redemption/object/DirectPaymentMetadataResponse'

export class RedemptionHistoryResponse extends JsonSerializable {
  /** @type {string} */
  #referenceLineItemId
  /** @type {number} */
  #orderTotal
  /** @type {number} */
  #quantity
  /** @type {string} */
  #brandName
  /** @type {number} */
  #lineItemSubtotal
  /** @type {number} */
  #lineItemFees
  /** @type {number} */
  #lineItemTotal
  /** @type {string} */
  #currencyCode
  /** @type {FulfillmentStatus} */
  #status
  /** @type {Date} */
  #createdAt
  /** @type {string} */
  #imageUrl
  /** @type {FulfillmentType} */
  #fulfillmentType
  /** @type {ItemRewardType} */
  #rewardType
  /** @type {DirectPaymentMetadataResponse} */
  #directPaymentMetadata
  /** @type {string} */
  #obfuscatedDeliveryEmailAddress
  /** @type {ItemDeliveryStatus} */
  #deliveryStatus
  /** @type {Date} */
  #deliveryDate

  constructor (jsonObject) {
    super()
    this.#referenceLineItemId = jsonObject.referenceLineItemId
    this.#orderTotal = Number.parseFloat(jsonObject.orderTotal) || undefined
    this.#quantity = Number.parseInt(jsonObject.quantity) || undefined
    this.#brandName = jsonObject.brandName
    this.#lineItemSubtotal = Number.parseFloat(jsonObject.lineItemSubtotal) || undefined
    this.#lineItemFees = Number.parseFloat(jsonObject.lineItemFees) || undefined
    this.#lineItemTotal = Number.parseFloat(jsonObject.lineItemTotal) || undefined
    this.#currencyCode = jsonObject.currencyCode
    this.#status = FulfillmentStatus.fromJSON(jsonObject.status)
    this.#createdAt = new Date(jsonObject.createdAt || undefined)
    this.#imageUrl = jsonObject.imageUrl
    this.#fulfillmentType = FulfillmentType.fromJSON(jsonObject.fulfillmentType)
    this.#rewardType = ItemRewardType.fromJSON({ description: jsonObject.rewardType })
    this.#directPaymentMetadata = DirectPaymentMetadataResponse.fromJSON(jsonObject.directPaymentMetadata)
    this.#obfuscatedDeliveryEmailAddress = jsonObject.obfuscatedDeliveryEmailAddress
    this.#deliveryStatus = ItemDeliveryStatus.fromJSON(jsonObject.deliveryStatus)
    this.#deliveryDate = new Date(jsonObject.deliveryDate || undefined)
    Object.freeze(this)
  }

  get referenceLineItemId () {
    return this.#referenceLineItemId
  }

  get orderTotal () {
    return this.#orderTotal
  }

  get quantity () {
    return this.#quantity
  }

  get brandName () {
    return this.#brandName
  }

  get lineItemSubtotal () {
    return this.#lineItemSubtotal
  }

  get lineItemFees () {
    return this.#lineItemFees
  }

  get lineItemTotal () {
    return this.#lineItemTotal
  }

  get currencyCode () {
    return this.#currencyCode
  }

  get status () {
    return this.#status
  }

  get createdAt () {
    return this.#createdAt
  }

  get imageUrl () {
    return this.#imageUrl
  }

  get fulfillmentType () {
    return this.#fulfillmentType
  }

  get rewardType () {
    return this.#rewardType
  }

  get directPaymentMetadata () {
    return this.#directPaymentMetadata
  }

  get obfuscatedDeliveryEmailAddress () {
    return this.#obfuscatedDeliveryEmailAddress
  }

  get deliveryStatus () {
    return this.#deliveryStatus
  }

  get deliveryDate () {
    return this.#deliveryDate
  }

  /**
   * @return {Object}
   */
  toJSON () {
    return {
      referenceLineItemId: this.#referenceLineItemId,
      orderTotal: this.#orderTotal,
      quantity: this.#quantity,
      brandName: this.#brandName,
      lineItemSubtotal: this.#lineItemSubtotal,
      lineItemFees: this.#lineItemFees,
      lineItemTotal: this.#lineItemTotal,
      currencyCode: this.#currencyCode,
      status: this.#status?.toJSON(),
      createdAt: this.#createdAt?.valueOf() || undefined,
      imageUrl: this.#imageUrl,
      fulfillmentType: this.#fulfillmentType?.toJSON(),
      rewardType: this.#rewardType?.toJSON()?.description,
      directPaymentMetadata: this.#directPaymentMetadata?.toJSON(),
      obfuscatedDeliveryEmailAddress: this.#obfuscatedDeliveryEmailAddress,
      deliveryStatus: this.#deliveryStatus?.toJSON(),
      deliveryDate: this.#deliveryDate?.valueOf() || undefined
    }
  }

  /**
   * @param {Object} jsonObject
   * @return {RedemptionHistoryResponse|undefined}
   */
  static fromJSON (jsonObject) {
    return (jsonObject && new RedemptionHistoryResponse(jsonObject)) || undefined
  }
}
